type LevelName = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
const LEVELS: Record<LevelName, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// 프로덕션이면 WARN 이상만, 아니면 DEBUG부터 찍는다
const isProd = process.env.REACT_APP_DEPLOY_TYPE === 'production';
const DEFAULT_MIN_LEVEL = isProd ? LEVELS.WARN : LEVELS.DEBUG;

// Transport 인터페이스: 로깅 출력을 담당
interface Transport {
  (level: LevelName, formatted: string, meta?: any): void;
}

const styleMap: Record<LevelName, string> = {
  DEBUG: 'color: #B5B5B5; font-weight: bold;',
  INFO: 'color: #1677FF; font-weight: bold;',
  WARN: 'color: #FAAD14; font-weight: bold;',
  ERROR: 'color: #FF4D4F; font-weight: bold;',
};

/**
 * 콘솔 전송
 * --
 * @param {LevelName} level
 * @param {string} formatted
 * @param {any} meta
 */
const consoleTransport: Transport = (level, formatted, meta) => {
  console.log(`%c${level}`, styleMap[level], `${formatted}`, meta ?? '');
};

export default class Logger {
  private name: string;
  private minLevel: number;
  private transports: Transport[];

  constructor(
    name: string,
    minLevel = DEFAULT_MIN_LEVEL,
    transports: Transport[] = [consoleTransport]
  ) {
    this.name = name;
    this.minLevel = minLevel;
    this.transports = transports;
  }

  private shouldLog(level: LevelName) {
    return LEVELS[level] >= this.minLevel;
  }

  /** V8 스택 API로 호출 지점의 file/func/line/col 정보를 구조화하여 리턴 */
  private getCallSiteInfo() {
    const orig = Error.prepareStackTrace;
    Error.prepareStackTrace = (_err, stack) => stack;
    const err = new Error();
    Error.captureStackTrace(err, this.getCallSiteInfo);
    const stack = (err.stack as unknown as NodeJS.CallSite[]) || [];
    Error.prepareStackTrace = orig;

    // 0:this, 1:log(), 2:debug()/info() 호출 지점
    const site = stack[3];

    return {
      file: site?.getFileName() ?? '<unknown>',
      func: site?.getFunctionName() ?? '<anonymous>',
      line: site?.getLineNumber() ?? 0,
      col: site?.getColumnNumber() ?? 0,
    };
  }

  private format(msg: any) {
    const ts = new Date().toISOString();
    // const { file, func, line, col } = this.getCallSiteInfo();

    // return `[${ts}] [${this.name}][${func}] [${msg}] — ${file}:${line}:${col}`;
    return `[${ts}][${this.name}][${msg}]`;
  }

  private log(level: LevelName, msg: any, meta?: any) {
    if (!this.shouldLog(level)) return;
    const formatted = this.format(msg);
    this.transports.forEach((t) => t(level, formatted, meta));
  }

  debug(msg: any, meta?: any) {
    this.log('DEBUG', msg, meta);
  }
  info(msg: any, meta?: any) {
    this.log('INFO', msg, meta);
  }
  warn(msg: any, meta?: any) {
    this.log('WARN', msg, meta);
  }
  error(msg: any, meta?: any) {
    this.log('ERROR', msg, meta);
  }
}
